import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, IconButton, Divider, Grid,
  useMediaQuery, useTheme, Chip, Snackbar, Alert, Tooltip
} from '@mui/material';
import { Close, CheckCircle, ShoppingCart, Warning } from '@mui/icons-material';
import './GoodReceiptHighlight.css';

const VerifyPurchaseModal = ({
  open,
  onClose,
  formData,
  purchaseDetails,
  onVerify,
  onActualVerify,
  suppliers,
  items,
  branches,
  loading
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [verifyConfirmOpen, setVerifyConfirmOpen] = useState(false);
  const [localPurchaseDetails, setLocalPurchaseDetails] = useState(purchaseDetails || []);
  const [originalPurchaseDetails, setOriginalPurchaseDetails] = useState(purchaseDetails || []);
  const [changedFields, setChangedFields] = useState({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  // Update local state when purchaseDetails prop changes
  useEffect(() => {
    // Ensure default values for quantity fields
    const detailsWithDefaults = (purchaseDetails || []).map(detail => ({
      ...detail,
      DamageQuantity: detail.DamageQuantity || 0,
      ExcessQuantity: detail.ExcessQuantity || 0,
      LessQuantity: detail.LessQuantity || 0
    }));
    setLocalPurchaseDetails(detailsWithDefaults);
    setOriginalPurchaseDetails(detailsWithDefaults);
    setChangedFields({});
    setCancelConfirmOpen(false);
  }, [purchaseDetails]);

  const handleDetailChange = (index, field, value) => {
    const updatedDetails = [...localPurchaseDetails];
    const receivedQty = parseFloat(updatedDetails[index].ReceivedQuantity) || 0;
    const numValue = parseFloat(value) || 0;
    
    // Validation based on Received Qty
    if (field === 'DamageQuantity') {
      // Damage quantity cannot exceed received quantity
      if (numValue > receivedQty) {
        alert(`Damage quantity (${numValue}) cannot exceed received quantity (${receivedQty})`);
        return;
      }
    }
    
    if (field === 'ExcessQuantity') {
      // Excess quantity should be reasonable (you can set a limit if needed)
      // For now, we'll allow any positive value
    }
    
    if (field === 'LessQuantity') {
      // Less quantity cannot exceed received quantity
      if (numValue > receivedQty) {
        alert(`Less quantity (${numValue}) cannot exceed received quantity (${receivedQty})`);
        return;
      }
    }
    
    updatedDetails[index] = { ...updatedDetails[index], [field]: numValue };
    
    // Track changed fields for highlighting
    const fieldKey = `${index}_${field}`;
    const originalValue = originalPurchaseDetails[index]?.[field] || 0;
    const newChangedFields = { ...changedFields };
    
    if (numValue !== originalValue) {
      newChangedFields[fieldKey] = true;
    } else {
      delete newChangedFields[fieldKey];
    }
    setChangedFields(newChangedFields);
    
    // Recalculate stock quantity when any quantity field changes
    if (field === 'DamageQuantity' || field === 'ExcessQuantity' || field === 'LessQuantity') {
      const damageQty = field === 'DamageQuantity' ? numValue : (parseFloat(updatedDetails[index].DamageQuantity) || 0);
      const excessQty = field === 'ExcessQuantity' ? numValue : (parseFloat(updatedDetails[index].ExcessQuantity) || 0);
      const lessQty = field === 'LessQuantity' ? numValue : (parseFloat(updatedDetails[index].LessQuantity) || 0);
      
      // Additional validation: Damage + Less cannot exceed Received + Excess
      const totalDeductions = damageQty + lessQty;
      const totalAvailable = receivedQty + excessQty;
      
      if (totalDeductions > totalAvailable) {
        alert(`Total damage (${damageQty}) + less quantity (${lessQty}) cannot exceed received (${receivedQty}) + excess (${excessQty}) quantities`);
        return;
      }
      
      // Stock Qty = Received Qty - Damage Qty + Excess Qty - Less Qty
      updatedDetails[index].StockQuantity = receivedQty - damageQty + excessQty - lessQty;
      
      // Also track stock quantity as changed if it's different from calculated original
      const originalStockQty = (originalPurchaseDetails[index]?.ReceivedQuantity || 0) - 
                              (originalPurchaseDetails[index]?.DamageQuantity || 0) + 
                              (originalPurchaseDetails[index]?.ExcessQuantity || 0) - 
                              (originalPurchaseDetails[index]?.LessQuantity || 0);
      const stockFieldKey = `${index}_StockQuantity`;
      
      if (updatedDetails[index].StockQuantity !== originalStockQty) {
        newChangedFields[stockFieldKey] = true;
      } else {
        delete newChangedFields[stockFieldKey];
      }
      setChangedFields(newChangedFields);
    }
    
    setLocalPurchaseDetails(updatedDetails);
  };

  const getFieldStyle = (index, field) => {
    const fieldKey = `${index}_${field}`;
    const isChanged = changedFields[fieldKey];
    
    // Different colors for different field types
    let borderColor = '#d1d5db';
    let backgroundColor = '#fff';
    let textColor = '#000';
    
    if (formData?.IsVerified) {
      backgroundColor = '#f3f4f6';
      textColor = '#6b7280';
    } else if (isChanged) {
      switch (field) {
        case 'DamageQuantity':
          borderColor = '#dc2626'; // Red border
          backgroundColor = '#fef2f2'; // Light red background
          textColor = '#991b1b'; // Dark red text
          break;
        case 'ExcessQuantity':
          borderColor = '#059669'; // Green border
          backgroundColor = '#f0fdf4'; // Light green background
          textColor = '#065f46'; // Dark green text
          break;
        case 'LessQuantity':
          borderColor = '#d97706'; // Orange border
          backgroundColor = '#fffbeb'; // Light orange background
          textColor = '#92400e'; // Dark orange text
          break;
        default:
          borderColor = '#6366f1'; // Blue border
          backgroundColor = '#eef2ff'; // Light blue background
          textColor = '#3730a3'; // Dark blue text
      }
    } else {
      // Default colors for unchanged fields
      if (field === 'DamageQuantity') {
        textColor = '#dc2626';
      }
    }
    
    return {
      width: '60px',
      padding: '4px', // Reduced padding to match Stock Qty height
      border: isChanged ? `1px solid ${borderColor}` : '1px solid #d1d5db', // Reduced border thickness
      borderRadius: '2px', // Reduced border radius to match Stock Qty
      textAlign: 'center',
      fontSize: '12px',
      backgroundColor,
      color: textColor,
      cursor: formData?.IsVerified ? 'not-allowed' : 'text',
      boxShadow: isChanged ? `0 0 0 1px ${borderColor}` : 'none',
      transition: 'all 0.2s ease-in-out',
      height: '24px' // Fixed height to match table row height
    };
  };

  const getStockQuantityStyle = (index) => {
    const fieldKey = `${index}_StockQuantity`;
    const isChanged = changedFields[fieldKey];
    
    return {
      padding: '4px 8px', // Reduced padding from '8px' to '4px 8px'
      textAlign: 'right',
      fontWeight: 600,
      color: isChanged ? '#7c3aed' : '#10b981', // Purple for changed, green for unchanged
      backgroundColor: isChanged ? '#f3e8ff' : 'transparent', // Light purple background when changed
      borderRadius: isChanged ? '2px' : '0', // Reduced border radius from '4px' to '2px'
      border: isChanged ? '1px solid #7c3aed' : 'none', // Reduced border from '2px' to '1px'
      transition: 'all 0.2s ease-in-out'
    };
  };

  const formatCurrency = (amt) => `₹${(amt || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getSupplierName = () => {
    if (!formData) return '-';
    if (formData.MasterName) return formData.MasterName;
    const supplier = suppliers.find(s => (s.MasterId || s.Id) === formData.MasterId);
    return supplier?.Name || '-';
  };

  const isIGSTInvoice = () => {
    if (!formData) return false;
    const invoiceType = (formData.InvoiceTypeName || '').toLowerCase();
    return invoiceType.includes('igst');
  };

  const handleUpdateAndSave = () => {
    if (formData && onVerify) {
      // Save directly without confirmation modal
      onVerify(formData.Id, localPurchaseDetails);
      setShowSuccessMessage(true);
      // Reset changed fields after successful save
      setTimeout(() => {
        setOriginalPurchaseDetails([...localPurchaseDetails]);
        setChangedFields({});
        setShowSuccessMessage(false);
      }, 2000);
    }
  };

  const handleConfirmVerify = () => {
    setVerifyConfirmOpen(false);
    if (formData && onActualVerify) {
      // Pass the updated purchase details for verification with stock creation
      onActualVerify(formData.Id, localPurchaseDetails);
    }
    onClose();
  };

  // Handle cancel with confirmation
  const handleCancelClick = () => {
    const hasChanges = Object.keys(changedFields).length > 0;
    if (hasChanges && !formData?.IsVerified) {
      setCancelConfirmOpen(true);
    } else {
      onClose();
    }
  };

  const handleConfirmCancel = () => {
    setCancelConfirmOpen(false);
    onClose();
  };

  const handleStayOnPage = () => {
    setCancelConfirmOpen(false);
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={(e, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            const hasChanges = Object.keys(changedFields).length > 0;
            if (hasChanges && !formData?.IsVerified) {
              setCancelConfirmOpen(true);
              return;
            }
          }
          onClose();
        }}
        maxWidth="lg" 
        fullWidth 
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '12px', maxHeight: '90vh', maxWidth: '1200px' } }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f0fdf4' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle sx={{ color: '#10b981', fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                Good Receipt
              </Typography>
              <Chip 
                label={formData?.IsVerified ? "Verified" : "Pending Receipt"} 
                size="small" 
                sx={{ 
                  fontSize: '0.65rem', 
                  height: '20px', 
                  bgcolor: formData?.IsVerified ? '#10b981' : '#fef3c7', 
                  color: formData?.IsVerified ? 'white' : '#92400e' 
                }} 
              />
            </Box>
            <IconButton onClick={handleCancelClick} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          {!formData ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="textSecondary">
                Loading purchase details...
              </Typography>
            </Box>
          ) : (
            <>
              {/* Purchase Information */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#10b981', mb: 1, display: 'block' }}>
                Purchase Information
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, p: 1.5, bgcolor: '#f0fdf4', borderRadius: 1 }}>
                <Box sx={{ minWidth: 120 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Bill Number</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#065f46' }}>
                    {formData.BillNumber || '-'}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 100 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Bill Date</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatDate(formData.BillDate)}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 150 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Supplier</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {getSupplierName()}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 120 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>GST Number</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formData.GSTNumber || '-'}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 100 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Total Amount</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#10b981' }}>
                    {formatCurrency(formData.TotalAmount)}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 100 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Clearing Date</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatDate(formData.ClearingDate)}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 120 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Stock Branch</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: formData.StockBranchId ? '#1e40af' : '#6b7280' }}>
                    {(branches || []).find(b => (b.Id || b.BranchId) === formData.StockBranchId)?.Name ||
                      (branches || []).find(b => (b.Id || b.BranchId) === formData.StockBranchId)?.BranchName ||
                      '-'}
                  </Typography>
                </Box>
              </Box>

              {/* Purchase Items */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#10b981', display: 'block' }}>
                  Purchase Items ({localPurchaseDetails?.length || 0} items)
                </Typography>
                {Object.keys(changedFields).length > 0 && (
                  <Chip 
                    label={`${Object.keys(changedFields).length} field(s) modified`}
                    size="small"
                    sx={{ 
                      fontSize: '0.65rem', 
                      height: '20px', 
                      bgcolor: '#e0f2fe', 
                      color: '#0277bd',
                      border: '1px solid #29b6f6'
                    }}
                  />
                )}
              </Box>
              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden', mb: 2 }}>
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr style={{ background: '#d1fae5' }}>
                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#065f46' }}>#</th>
                        <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#065f46' }}>Item</th>
                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#065f46' }}>HSN</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#065f46' }}>Rcvd Qty</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#065f46' }}>Dmg Qty</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#065f46' }}>Excess Qty</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#065f46' }}>Less Qty</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#065f46' }}>Stock Qty</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#065f46' }}>Rate</th>
                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#065f46' }}>Disc%</th>
                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#065f46' }}>GST%</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#065f46' }}>Taxable</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#065f46' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(localPurchaseDetails || []).map((detail, index) => (
                        <tr key={detail.Id || index} style={{ 
                          borderBottom: '1px solid #e2e8f0', 
                          background: index % 2 === 0 ? '#fff' : '#f0fdf4' 
                        }}>
                          <td style={{ padding: '8px', textAlign: 'center' }}>{index + 1}</td>
                          <td style={{ padding: '8px', fontWeight: 500 }}>
                            {detail.ItemName || items.find(i => i.Id === detail.ItemId)?.Name || '-'}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>{detail.HSNCode || '-'}</td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>
                            {detail.ReceivedQuantity || 0}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>
                            <input
                              type="number"
                              value={detail.DamageQuantity ?? 0}
                              onChange={(e) => handleDetailChange(index, 'DamageQuantity', e.target.value)}
                              onFocus={(e) => e.target.select()}
                              disabled={formData?.IsVerified}
                              style={getFieldStyle(index, 'DamageQuantity')}
                              min="0"
                              max={detail.ReceivedQuantity || 0}
                              step="0.01"
                              title={formData?.IsVerified ? 'Purchase is verified - cannot edit' : `Maximum: ${detail.ReceivedQuantity || 0}`}
                            />
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>
                            <input
                              type="number"
                              value={detail.ExcessQuantity ?? 0}
                              onChange={(e) => handleDetailChange(index, 'ExcessQuantity', e.target.value)}
                              onFocus={(e) => e.target.select()}
                              disabled={formData?.IsVerified}
                              style={getFieldStyle(index, 'ExcessQuantity')}
                              min="0"
                              step="0.01"
                              title={formData?.IsVerified ? 'Purchase is verified - cannot edit' : 'Enter excess quantity received'}
                            />
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>
                            <input
                              type="number"
                              value={detail.LessQuantity ?? 0}
                              onChange={(e) => handleDetailChange(index, 'LessQuantity', e.target.value)}
                              onFocus={(e) => e.target.select()}
                              disabled={formData?.IsVerified}
                              style={getFieldStyle(index, 'LessQuantity')}
                              min="0"
                              max={detail.ReceivedQuantity || 0}
                              step="0.01"
                              title={formData?.IsVerified ? 'Purchase is verified - cannot edit' : `Maximum: ${detail.ReceivedQuantity || 0}`}
                            />
                          </td>
                          <td style={getStockQuantityStyle(index)}>
                            {(detail.ReceivedQuantity || 0) - (detail.DamageQuantity || 0) + (detail.ExcessQuantity || 0) - (detail.LessQuantity || 0)}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>
                            {formatCurrency(detail.Rate)}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            {detail.DiscountPercentage || 0}%
                          </td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            {detail.GSTRate || 0}%
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>
                            {formatCurrency(detail.TaxableAmount)}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#10b981' }}>
                            {formatCurrency(detail.TotalAmount)}
                          </td>
                        </tr>
                      ))}
                      {(!localPurchaseDetails || localPurchaseDetails.length === 0) && (
                        <tr>
                          <td colSpan="13" style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                            No items found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Box>
              </Box>

              {/* Purchase Summary */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#10b981', mb: 1, display: 'block' }}>
                Purchase Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ bgcolor: '#f0fdf4', borderRadius: 1, p: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Sub Total:</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        {formatCurrency(formData.Amount)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Discount:</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, color: '#dc2626' }}>
                        - {formatCurrency(formData.DiscountAmount)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Taxable:</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        {formatCurrency(formData.TaxableAmount)}
                      </Typography>
                    </Box>
                    {!isIGSTInvoice() ? (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">CGST:</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 500, color: '#166534' }}>
                            {formatCurrency(formData.CGSTAmount)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">SGST:</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 500, color: '#166534' }}>
                            {formatCurrency(formData.SGSTAmount)}
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">IGST:</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#166534' }}>
                          {formatCurrency(formData.IGSTAmount)}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Remarks - Left Column */}
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#10b981', mb: 1, mt: 2, display: 'block' }}>
                    Remarks
                  </Typography>
                  <Box sx={{ bgcolor: '#f0fdf4', borderRadius: 1, p: 1.5, mb: 2 }}>
                    <Typography variant="body2" sx={{ color: '#374151' }}>
                      {formData.Remarks || 'Nil'}
                    </Typography>
                  </Box>

                  {/* Verification Warning - Left Column */}
                  <Box sx={{ 
                    bgcolor: '#fef3c7', 
                    border: '1px solid #f59e0b', 
                    borderRadius: 1, 
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <Warning sx={{ color: '#f59e0b', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: '#92400e' }}>
                      <strong>Important:</strong> Once receipt is processed, this purchase cannot be edited or modified. 
                      Please review all details carefully before proceeding with the receipt process.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ bgcolor: '#f0fdf4', borderRadius: 1, p: 1.5 }}>
                    {(parseFloat(formData.FreightAmount) || 0) > 0 && (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">Freight:</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            {formatCurrency(formData.FreightAmount)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">Freight GST:</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            {formatCurrency(formData.FreightGSTAmount)}
                          </Typography>
                        </Box>
                      </>
                    )}
                    {(parseFloat(formData.PackingAmount) || 0) > 0 && (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">Packing:</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            {formatCurrency(formData.PackingAmount)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">Packing GST:</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            {formatCurrency(formData.PackingGSTAmount)}
                          </Typography>
                        </Box>
                      </>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">TCS:</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        {formatCurrency(formData.TCSAmount)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Round Off:</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        {formData.RoundOff >= 0 ? '+' : ''}{(formData.RoundOff || 0).toFixed(2)}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#10b981' }}>Grand Total:</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#10b981', fontSize: '0.9rem' }}>
                        {formatCurrency(formData.TotalAmount)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Paid:</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, color: '#10b981' }}>
                        {formatCurrency(formData.PaidAmount)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>Balance:</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#dc2626' }}>
                        {formatCurrency(formData.BalanceAmount)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f0fdf4' }}>
          <Button 
            onClick={handleCancelClick} 
            variant="outlined" 
            size="small" 
            sx={{ 
              borderColor: '#10b981', 
              color: '#10b981', 
              '&:hover': { borderColor: '#059669', bgcolor: '#d1fae5' } 
            }}
          >
            {formData?.IsVerified ? 'Close' : 'Cancel'}
          </Button>
          {formData && !formData.IsVerified && (
            <>
              <Tooltip 
                title={Object.keys(changedFields).length === 0 ? "No changes to save" : "Save changes to quantities"}
                arrow
              >
                <span>
                  <Button 
                    onClick={handleUpdateAndSave}
                    variant="contained" 
                    size="small" 
                    disabled={loading || Object.keys(changedFields).length === 0}
                    sx={{ 
                      bgcolor: Object.keys(changedFields).length === 0 ? '#9ca3af' : '#10b981', 
                      '&:hover': { bgcolor: Object.keys(changedFields).length === 0 ? '#9ca3af' : '#059669' },
                      '&:disabled': { bgcolor: '#9ca3af', color: '#6b7280' },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    <CheckCircle sx={{ fontSize: 16 }} />
                    Update & Save
                  </Button>
                </span>
              </Tooltip>
              <Button 
                onClick={() => setVerifyConfirmOpen(true)}
                variant="contained" 
                size="small" 
                disabled={loading}
                sx={{ 
                  bgcolor: '#8b5cf6', 
                  '&:hover': { bgcolor: '#7c3aed' },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <CheckCircle sx={{ fontSize: 16 }} />
                Verify Purchase
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Success Message Snackbar */}
      <Snackbar 
        open={showSuccessMessage} 
        autoHideDuration={3000} 
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowSuccessMessage(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          Good Receipt updated successfully! Changes have been saved.
        </Alert>
      </Snackbar>

      {/* Cancel Confirmation Modal */}
      <Dialog
        open={cancelConfirmOpen}
        onClose={handleStayOnPage}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ 
              backgroundColor: '#fef3c7', 
              borderRadius: '50%', 
              width: 40, 
              height: 40, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <Warning sx={{ color: '#f59e0b', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
              Unsaved Changes
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            You have unsaved changes to the Good Receipt quantities that will be lost if you leave this page.
          </Typography>
          <Typography sx={{ color: '#dc2626', fontSize: '0.85rem', mt: 1, fontWeight: 500 }}>
            Are you sure you want to exit without saving your changes?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleStayOnPage}
            variant="outlined"
            sx={{ 
              borderColor: '#10b981', 
              color: '#10b981', 
              '&:hover': { borderColor: '#059669', backgroundColor: '#f0fdf4' } 
            }}
          >
            Stay on Page
          </Button>
          <Button
            onClick={handleConfirmCancel}
            variant="contained"
            sx={{ backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}
          >
            Exit Without Saving
          </Button>
        </DialogActions>
      </Dialog>

      {/* Verification Confirmation Dialog */}
      <Dialog
        open={verifyConfirmOpen}
        onClose={() => setVerifyConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ 
              backgroundColor: '#f0fdf4', 
              borderRadius: '50%', 
              width: 40, 
              height: 40, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <CheckCircle sx={{ color: '#10b981', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
              Confirm Receipt
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            Are you sure you want to process Good Receipt for <strong style={{ color: '#1e293b' }}>
              {formData?.BillNumber || 'this purchase'}
            </strong>? 
          </Typography>
          <Typography sx={{ color: '#dc2626', fontSize: '0.85rem', mt: 1, fontWeight: 500 }}>
            This will create stock entries and mark the purchase as verified. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setVerifyConfirmOpen(false)}
            variant="outlined"
            sx={{ 
              borderColor: '#e2e8f0', 
              color: '#64748b', 
              '&:hover': { borderColor: '#cbd5e1', backgroundColor: '#f8fafc' } 
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmVerify}
            variant="contained"
            sx={{ backgroundColor: '#10b981', '&:hover': { backgroundColor: '#059669' } }}
          >
            Verify & Complete Receipt
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VerifyPurchaseModal;
